import {
  type CurrentPrice,
  PRICE_CHANGE_STATUSES,
  type PriceChangeRequest,
  type PriceChangeStatus,
  priceChangeStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  type Tone,
  useToast,
} from "@station/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { CalendarClock, Tags } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { PriceGroupsPanel } from "../../../features/price-groups.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel } from "../../../lib/excel.js";
import { formatDateTime, formatPriceChange, formatUnitPrice } from "../../../lib/format.js";
import { useDocumentTitle } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { useScope } from "../../../lib/scope.js";
import { usePermissions } from "../../../lib/session.js";
import { useStepUp } from "../../../lib/step-up.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const searchSchema = z.object({
  tab: z.enum(["current", "requests", "groups"]).optional().catch(undefined),
  status: priceChangeStatusSchema.optional().catch(undefined),
  request: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/fiyatlar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PricesPage,
});

const REQUEST_TONE: Record<PriceChangeStatus, Tone> = {
  DRAFT: "neutral",
  PENDING_APPROVAL: "warning",
  SCHEDULED: "info",
  APPLIED: "success",
  CANCELLED: "neutral",
};

function PricesPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = usePermissions();
  useDocumentTitle(t("menu.prices"));

  return (
    <>
      <PageHeader
        title={t("menu.prices")}
        description={t("pricing.description")}
        breadcrumb={[{ label: t("menu.groups.catalog") }, { label: t("menu.prices") }]}
        actions={
          permissions.has("fuel.price.change") ? (
            <Button asChild>
              <Link to="/fiyatlar/toplu-degisiklik">
                <CalendarClock aria-hidden="true" />
                {t("pricing.bulk.action")}
              </Link>
            </Button>
          ) : null
        }
      />
      <Tabs
        value={search.tab ?? "current"}
        onValueChange={(tab) =>
          void navigate({ search: { tab: tab as "current" | "requests" | "groups" }, replace: true })
        }
      >
        <TabsList aria-label={t("menu.prices")}>
          <TabsTrigger value="current">{t("pricing.tabs.current")}</TabsTrigger>
          <TabsTrigger value="requests">{t("pricing.tabs.requests")}</TabsTrigger>
          <TabsTrigger value="groups">{t("pricing.tabs.groups")}</TabsTrigger>
        </TabsList>
        <TabsContent value="current">
          <CurrentPrices />
        </TabsContent>
        <TabsContent value="groups">
          {/* Fiyat grupları (Prompt 16): toplu fiyat değişikliğinde istasyon seçim kısayolu. */}
          <PriceGroupsPanel />
        </TabsContent>
        <TabsContent value="requests">
          <Requests
            status={search.status}
            openId={search.request}
            onOpen={(id) =>
              void navigate({
                search: (previous) => ({ ...previous, tab: "requests", request: id }),
                replace: true,
              })
            }
            onStatus={(status) =>
              void navigate({ search: { tab: "requests", ...(status ? { status } : {}) }, replace: true })
            }
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

/** İstasyon × ürün matrisi: güncel fiyat, yürürlük tarihi ve planlanmış sıradaki fiyat. Hücreye tıklayınca geçmiş açılır. */
function CurrentPrices(): ReactElement {
  const { t } = useTranslation();
  const scope = useScope();
  const shape = useTenantShape();
  const [selected, setSelected] = useState<CurrentPrice | null>(null);
  const stationId = scope.kind === "station" ? scope.stationId : undefined;
  const prices = useQuery({
    queryKey: queryKeys.prices({ stationId: stationId ?? null }),
    queryFn: async () =>
      (await api.GET("/prices/current", { params: { query: stationId ? { stationId } : {} } })).data!,
  });

  if (prices.error) return <Alert tone="danger">{errorMessage(prices.error)}</Alert>;
  if (!prices.data) return <SkeletonRows rows={6} />;
  const items = prices.data.items;
  if (items.length === 0)
    return (
      <EmptyState
        icon={<Tags />}
        title={t("pricing.current.empty")}
        description={t("pricing.current.emptyHint")}
      />
    );

  const products = [...new Map(items.map((item) => [item.productId, item])).values()];
  const stations = [...new Map(items.map((item) => [item.stationId, item])).values()];
  const cell = new Map(items.map((item) => [`${item.stationId}:${item.productId}`, item]));

  return (
    <>
      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">{t("pricing.current.caption")}</caption>
          <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-muted px-3 py-3 text-left font-semibold">
                {shape.isSingleStation ? t("menu.station") : t("menu.stations")}
              </th>
              {products.map((product) => (
                <th
                  key={product.productId}
                  scope="col"
                  className="min-w-32 px-3 py-3 text-right font-semibold"
                >
                  <span className="inline-flex items-center gap-1.5">
                    {product.productColor ? (
                      <span
                        className="size-2.5 rounded-full border"
                        style={{ backgroundColor: product.productColor }}
                        aria-hidden="true"
                      />
                    ) : null}
                    {product.productName}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stations.map((station) => (
              <tr key={station.stationId} className="border-t">
                <th scope="row" className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-medium">
                  <span className="font-mono text-xs text-muted-foreground">{station.stationCode}</span>{" "}
                  {station.stationName}
                </th>
                {products.map((product) => {
                  const item = cell.get(`${station.stationId}:${product.productId}`);
                  return (
                    <td key={product.productId} className="px-1 py-1 text-right align-top">
                      {item ? (
                        <button
                          type="button"
                          onClick={() => setSelected(item)}
                          className="grid w-full justify-items-end rounded-md px-2 py-1 hover:bg-muted"
                          aria-label={t("pricing.current.cellLabel", {
                            station: station.stationName,
                            product: product.productName,
                            price: formatUnitPrice(item.unitPrice),
                          })}
                        >
                          <span className={item.unitPrice ? "font-semibold tabular" : "text-xs text-warning"}>
                            {item.unitPrice ? formatUnitPrice(item.unitPrice) : t("pricing.current.noPrice")}
                          </span>
                          {item.upcoming ? (
                            <span className="text-xs text-info tabular">
                              → {formatUnitPrice(item.upcoming.unitPrice)} ·{" "}
                              {formatDateTime(item.upcoming.effectiveAt)}
                            </span>
                          ) : null}
                        </button>
                      ) : (
                        <span className="px-2 text-muted-foreground">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="border-t px-3 py-2 text-xs text-muted-foreground">{t("pricing.current.legend")}</p>
      </Card>
      <Drawer
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={selected ? `${selected.productName} — ${selected.stationName}` : ""}
        description={t("pricing.history.description")}
      >
        {selected ? <PriceHistory item={selected} /> : null}
      </Drawer>
    </>
  );
}

function PriceHistory({ item }: { item: CurrentPrice }): ReactElement {
  const { t } = useTranslation();
  const pages = useCursorPages(`${item.stationId}:${item.productId}`);
  const history = useQuery({
    queryKey: [...queryKeys.priceHistory(item.stationId, item.productId), pages.cursor ?? null],
    queryFn: async () =>
      (
        await api.GET("/prices/history", {
          params: {
            query: {
              stationId: item.stationId,
              productId: item.productId,
              limit: 20,
              ...(pages.cursor ? { cursor: pages.cursor } : {}),
            },
          },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });
  const rows = history.data?.items ?? [];
  type Row = (typeof rows)[number];
  const columns: DataTableColumn<Row>[] = [
    {
      id: "validFrom",
      header: t("pricing.history.validFrom"),
      hideable: false,
      cell: (row) => <span className="tabular">{formatDateTime(row.validFrom)}</span>,
      exportValue: (row) => formatDateTime(row.validFrom),
    },
    {
      id: "unitPrice",
      header: t("pricing.unitPrice"),
      align: "right",
      cell: (row) => <span className="font-semibold tabular">{formatUnitPrice(row.unitPrice)}</span>,
      exportValue: (row) => Number(row.unitPrice),
    },
    {
      id: "source",
      header: t("pricing.source"),
      cell: (row) => t(`enums.priceSource.${row.source}`),
      exportValue: (row) => t(`enums.priceSource.${row.source}`),
    },
    {
      id: "requestNo",
      header: t("pricing.requestNo"),
      cell: (row) => <span className="font-mono text-xs">{row.requestNo ?? "—"}</span>,
      exportValue: (row) => row.requestNo,
    },
  ];
  return (
    <div className="grid gap-4">
      {item.upcoming ? (
        <Alert tone="info" title={t("pricing.history.upcomingTitle")}>
          {t("pricing.history.upcoming", {
            price: formatUnitPrice(item.upcoming.unitPrice),
            date: formatDateTime(item.upcoming.effectiveAt),
            requestNo: item.upcoming.requestNo,
          })}
        </Alert>
      ) : null}
      <DataTable
        caption={t("pricing.history.description")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={history.isPending}
        error={history.error ? <Alert tone="danger">{errorMessage(history.error)}</Alert> : undefined}
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: history.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(history.data?.page.nextCursor),
        }}
        onExport={(visible) =>
          exportRowsToExcel({
            fileName: `fiyat-gecmisi-${item.stationCode}-${item.productCode}`,
            sheetName: t("pricing.history.description"),
            columns: visible,
            rows,
          })
        }
      />
    </div>
  );
}

function Requests({
  status,
  openId,
  onOpen,
  onStatus,
}: {
  status: PriceChangeStatus | undefined;
  openId: string | undefined;
  onOpen: (id: string | undefined) => void;
  onStatus: (status: PriceChangeStatus | undefined) => void;
}): ReactElement {
  const { t } = useTranslation();
  const pages = useCursorPages(status ?? "");
  const requests = useQuery({
    queryKey: queryKeys.priceChangeRequests({ status: status ?? null, cursor: pages.cursor ?? null }),
    queryFn: async () =>
      (
        await api.GET("/price-change-requests", {
          params: {
            query: {
              limit: 25,
              ...(status ? { status } : {}),
              ...(pages.cursor ? { cursor: pages.cursor } : {}),
            },
          },
        })
      ).data!,
    placeholderData: keepPreviousData,
  });
  const columns = useMemo<DataTableColumn<PriceChangeRequest>[]>(
    () => [
      {
        id: "requestNo",
        header: t("pricing.requestNo"),
        hideable: false,
        cell: (row) => <span className="font-mono font-medium">{row.requestNo}</span>,
      },
      {
        id: "effectiveAt",
        header: t("pricing.effectiveAt"),
        cell: (row) => <span className="tabular">{formatDateTime(row.effectiveAt)}</span>,
      },
      {
        id: "scope",
        header: t("pricing.requests.scope"),
        cell: (row) => t("pricing.requests.scopeValue", { stations: row.stationCount, items: row.itemCount }),
      },
      { id: "source", header: t("pricing.source"), cell: (row) => t(`enums.priceSource.${row.source}`) },
      { id: "createdBy", header: t("pricing.requests.createdBy"), cell: (row) => row.createdByName ?? "—" },
      {
        id: "status",
        header: t("common.status"),
        cell: (row) => (
          <StatusBadge tone={REQUEST_TONE[row.status]}>
            {t(`enums.priceChangeStatus.${row.status}`)}
          </StatusBadge>
        ),
      },
    ],
    [t],
  );
  return (
    <>
      <DataTable
        caption={t("pricing.tabs.requests")}
        columns={columns}
        rows={requests.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={requests.isPending}
        error={requests.error ? <Alert tone="danger">{errorMessage(requests.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            icon={<CalendarClock />}
            title={t("pricing.requests.empty")}
            description={t("pricing.requests.emptyHint")}
          />
        }
        filters={
          <FormField label={t("common.status")} className="w-48">
            {(control) => (
              <Select
                {...control}
                value={status ?? ""}
                onChange={(event) =>
                  onStatus((event.target.value || undefined) as PriceChangeStatus | undefined)
                }
              >
                <option value="">{t("common.all")}</option>
                {PRICE_CHANGE_STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(`enums.priceChangeStatus.${value}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: requests.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(requests.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onRowClick={(row) => onOpen(row.id)}
      />
      <Drawer
        open={openId !== undefined}
        onOpenChange={(open) => !open && onOpen(undefined)}
        title={t("pricing.requests.detailTitle")}
        className="max-w-2xl"
      >
        {openId ? <RequestDetail key={openId} id={openId} /> : null}
      </Drawer>
    </>
  );
}

function RequestDetail({ id }: { id: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const withStepUp = useStepUp();
  const permissions = usePermissions();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const request = useQuery({
    queryKey: queryKeys.priceChangeRequest(id),
    queryFn: async () => (await api.GET("/price-change-requests/{id}", { params: { path: { id } } })).data!,
  });
  // Bu belge için BANA karar düşen bekleyen bir onay talebi var mı? (Talep sahibinde liste boş döner.)
  const decidable = useQuery({
    queryKey: queryKeys.approvals("requests", { docId: id, scope: "MINE_TO_DECIDE" }),
    enabled: request.data?.status === "PENDING_APPROVAL",
    queryFn: async () =>
      (
        await api.GET("/approval-requests", {
          params: {
            query: { scope: "MINE_TO_DECIDE" as const, docType: "PRICE_CHANGE" as const, limit: 100 },
          },
        })
      ).data!.items.some((item) => item.docId === id),
  });

  const act = useMutation({
    mutationFn: async (action: "submit" | "approve" | "cancel") => {
      const params = { path: { id } };
      if (action === "submit") return api.POST("/price-change-requests/{id}/submit", { params });
      // Onay hassas bir işlemdir: gerekirse yeniden kimlik doğrulama penceresi açılır ve işlem kendiliğinden yinelenir.
      if (action === "approve")
        return withStepUp(() => api.POST("/price-change-requests/{id}/approve", { params }));
      return withStepUp(() => api.POST("/price-change-requests/{id}/cancel", { params, body: { reason } }));
    },
    onSuccess: async (_data, action) => {
      setCancelling(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.priceChangeRequests() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.prices() }),
      ]);
      toast({ tone: "success", title: t(`pricing.requests.done.${action}`) });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  if (request.error) return <Alert tone="danger">{errorMessage(request.error)}</Alert>;
  if (!request.data) return <SkeletonRows rows={5} />;
  const data = request.data;
  const canChange = permissions.has("fuel.price.change");
  /**
   * Onay yetkisi ARTIK onay motorundan gelir: `fuel.price.approve` izni olsa da kimse KENDİ talebini onaylayamaz
   * (sunucu 403 döner). Fiyat talebi yanıtı onay talebi kimliği taşımadığı için en az maliyetli yol seçildi:
   * "bana bekleyenler" kapsamında bu belgeye açılmış bekleyen talep var mı diye bakılır (uç izin aramaz).
   */
  const canApprove = permissions.has("fuel.price.approve");
  const canDecide = canApprove && decidable.data === true;

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-lg font-semibold">{data.requestNo}</span>
        <StatusBadge tone={REQUEST_TONE[data.status]}>
          {t(`enums.priceChangeStatus.${data.status}`)}
        </StatusBadge>
      </div>
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        {(
          [
            [t("pricing.effectiveAt"), formatDateTime(data.effectiveAt)],
            [t("pricing.source"), t(`enums.priceSource.${data.source}`)],
            [t("pricing.requests.createdBy"), data.createdByName ?? "—"],
            [
              t("pricing.requests.approvedBy"),
              data.approvedByName ? `${data.approvedByName} · ${formatDateTime(data.approvedAt)}` : "—",
            ],
            ...(data.appliedAt ? [[t("pricing.requests.appliedAt"), formatDateTime(data.appliedAt)]] : []),
            ...(data.cancelReason ? [[t("pricing.requests.cancelReason"), data.cancelReason]] : []),
            ...(data.note ? [[t("pricing.requests.note"), data.note]] : []),
          ] as Array<[string, string]>
        ).map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="font-medium">{value}</dd>
          </div>
        ))}
      </dl>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <caption className="sr-only">{t("pricing.requests.items")}</caption>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                {t("menu.station")}
              </th>
              <th scope="col" className="px-3 py-2 text-left">
                {t("pricing.product")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {data.status === "APPLIED"
                  ? t("pricing.requests.previous")
                  : t("pricing.requests.currentPrice")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("pricing.requests.newPrice")}
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                {t("pricing.requests.change")}
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={`${item.stationId}:${item.productId}`} className="border-t tabular">
                <td className="px-3 py-2">
                  <span className="font-mono text-xs text-muted-foreground">{item.stationCode}</span>{" "}
                  {item.stationName}
                </td>
                <td className="px-3 py-2">{item.productName}</td>
                <td className="px-3 py-2 text-right text-muted-foreground">
                  {formatUnitPrice(item.previousUnitPrice)}
                </td>
                <td className="px-3 py-2 text-right font-semibold">{formatUnitPrice(item.newUnitPrice)}</td>
                <td className="px-3 py-2 text-right text-xs">
                  {formatPriceChange(item.previousUnitPrice, item.newUnitPrice)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        {(data.status === "DRAFT" || data.status === "PENDING_APPROVAL") && canChange ? (
          <Button variant="outline" onClick={() => setCancelling(true)}>
            {t("pricing.requests.cancel")}
          </Button>
        ) : null}
        {data.status === "SCHEDULED" && canApprove ? (
          <Button variant="destructive" onClick={() => setCancelling(true)}>
            {t("pricing.requests.cancelScheduled")}
          </Button>
        ) : null}
        {data.status === "DRAFT" && canChange ? (
          <Button onClick={() => act.mutate("submit")} loading={act.isPending}>
            {t("pricing.requests.submit")}
          </Button>
        ) : null}
        {data.status === "PENDING_APPROVAL" && canDecide ? (
          <Button onClick={() => act.mutate("approve")} loading={act.isPending}>
            {t("pricing.requests.approve")}
          </Button>
        ) : null}
      </div>
      {data.status === "PENDING_APPROVAL" && !canDecide ? (
        <Alert tone="info">
          {canApprove ? t("pricing.requests.cannotApproveOwn") : t("pricing.requests.waitingApproval")}
        </Alert>
      ) : null}

      <Dialog
        open={cancelling}
        onOpenChange={setCancelling}
        title={t("pricing.requests.cancelTitle")}
        description={t("pricing.requests.cancelDescription")}
      >
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate("cancel");
          }}
        >
          <FormField label={t("pricing.requests.cancelReason")} required>
            {(control) => (
              <Textarea
                {...control}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                maxLength={300}
                autoFocus
              />
            )}
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCancelling(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              loading={act.isPending}
              disabled={reason.trim().length < 3}
            >
              {t("pricing.requests.cancel")}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
